/*
 * Sticky buy bar.
 *
 * The bar is pure CSS. assets/ap-product-system.css pins it with
 * `position: fixed; bottom: 0` at every width, so it needs no script to appear.
 * What it did without a script was appear from the first paint, which put a
 * second "Add to cart" on screen underneath the real one for the whole of the
 * buy block.
 *
 * So this only takes the bar away — while the controls it duplicates are on
 * screen, while the shopper has not reached them yet, and over the footer. It
 * is subtraction, not the mechanism: the two attributes below are set by
 * nothing else.
 *
 * The bar being `fixed` is why the footer is a suppress anchor. It used to be
 * `sticky` inside the product section's `.page-width`, so it could not reach
 * the footer because it could not reach anything past the buy column — which
 * is the defect that cost it two thirds of the page. Containment by accident
 * of the DOM is now containment by rule.
 *
 * Two kinds of anchor, and the difference matters:
 *
 *   buy anchors (data-ap-sticky-anchors) — the controls the bar stands in for.
 *     The bar hides while any is on screen AND while every one of them is still
 *     below the viewport, because "not scrolled past" and "not yet reached"
 *     look identical to `isIntersecting` alone and mean opposite things. On the
 *     real PDP the add-to-cart sits ~1200px (desktop) / ~2000px (mobile) down
 *     the buy column, below the eyebrow, title, description, price, variant
 *     picker, quantity and the Recharge block — so at first paint it is off
 *     screen and the old rule showed the bar while the shopper was still
 *     reading the title. `boundingClientRect.top` is what tells them apart.
 *
 *   suppress anchors (data-ap-sticky-suppress) — regions the bar must not
 *     cover. Hide while on screen, and nothing more; a suppressor below the
 *     viewport says nothing about whether the buy block has been passed.
 *
 * IntersectionObserver against the real controls, not a scroll listener with a
 * pixel offset. The buy block's position moves with the variant picker, the
 * dosage band and the Recharge widget — all of which are per-product — so any
 * fixed offset would be wrong on the first product that renders one of them
 * differently.
 *
 * Nothing boots from DOMContentLoaded: the theme editor replaces the section's
 * outerHTML on every edit, and a one-shot listener would leave a dead bar
 * behind. State lives on the element and binds in connectedCallback.
 */

class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    // The theme editor is the one place the bar should stay put. A merchant who
    // ticks "Sticky add to cart" needs to see what it did, and the
    // scroll position that would reveal it may be nowhere near the block they
    // are editing. Leaving it visible there is also exactly the pre-script
    // behaviour, so the editor never shows less than the storefront.
    if (this.dataset.designMode === 'true') return;

    this.anchors = this.readSelectors(this.dataset.apStickyAnchors);

    // No buy block on the page — a merchant can remove it in the editor. Leave
    // the CSS bar alone rather than hiding a bar that nothing would bring back.
    if (!this.anchors.length) return;

    this.suppressors = this.readSelectors(this.dataset.apStickySuppress);

    this.onScreen = new Set();
    // Every buy anchor starts assumed-unreached, which is true of a page loaded
    // at the top and is the safe answer for one that is not: the first observer
    // callback corrects it before paint, and until then the bar stays down.
    this.below = new Set(this.anchors);
    this.toggleAttribute('data-ap-sticky-hidden', true);

    this.observer?.disconnect();
    this.observer = new IntersectionObserver((entries) => this.update(entries), { threshold: 0 });
    this.anchors.concat(this.suppressors).forEach((target) => this.observer.observe(target));
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  readSelectors(value) {
    return (value || '')
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean)
      .map((selector) => document.querySelector(selector))
      .filter(Boolean);
  }

  /*
   * No rootMargin. An anchor anywhere in the viewport keeps the bar away,
   * including in the last few pixels of it, which is where the bar would
   * otherwise sit on top of the thing it is standing in for. On a sold-out
   * product the back-in-stock form is an anchor too, for the same reason.
   */
  update(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.onScreen.add(entry.target);
      else this.onScreen.delete(entry.target);

      if (!this.anchors.includes(entry.target)) return;

      // A positive top on something off screen means it is below the fold: the
      // shopper is above the buy block, not past it. Negative means passed.
      if (!entry.isIntersecting && entry.boundingClientRect.top > 0) this.below.add(entry.target);
      else this.below.delete(entry.target);
    });

    // Every buy anchor still below the fold is the not-yet-reached case. Any
    // one of them passed is enough to have earned the bar, which is what makes
    // a sold-out product behave: pass the add-to-cart, get the bar, and the
    // back-in-stock form takes it away again on its own when it arrives.
    const unreached = this.below.size === this.anchors.length;

    this.toggleAttribute('data-ap-sticky-hidden', this.onScreen.size > 0 || unreached);

    // The transition is scoped to this attribute so the boot-time hide above is
    // instant. Without it every page load starts with the bar sliding off.
    this.toggleAttribute('data-ap-sticky-ready', true);
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
